import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { api } from "@/lib/api";

export interface EmployeeRangeFilter {
  min: number;
  max?: number;
}

export type PdfStatus = "not_generated" | "generating" | "generated" | "failed";
export type AnalysisStatus = "unlocked" | "in_progress" | "completed";

export interface CatalogCompanySummary {
  _id: string;
  companyName: string;
  city: string;
  country: string;
  industry: string;
  employees: number;
  shortDescription: string;
  logoUrl: string;
  companyKey?: string;
  pdfStatus?: PdfStatus;
  analysisStatus?: AnalysisStatus;
  executiveSnapshotFitScore?: number | null;
  analysisCreatedAt?: string | null;
}

interface UserCompaniesResponse {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  companies: CatalogCompanySummary[];
}

interface CatalogSearchResponse {
  total: number;
  totalPages: number;
  data: CatalogCompanySummary[];
}

export interface CompanyAccessRecord {
  cognitoUserId: string;
  companyKey: string;
  analysisStatus: AnalysisStatus;
  pdfStatus: PdfStatus;
  salesOutreachId?: string;
  comparison?: string;
  challenges?: string;
  value?: string;
  contactName?: string;
  contactLastName?: string;
  contactTitle?: string;
  contactCity?: string;
  contactCountry?: string;
  contactLink?: string;
  outreachMessage?: string;
  strategicFitLevel?: "low" | "medium" | "high";
  strategicFitSummary?: string;
  strategicFitServices?: Array<{ name: string; description: string; fitLevel: "High" | "Medium" }>;
  strategicFitObjections?: string[];
  buyingEntryDecisionMaker?: string;
  buyingEntryDecisionMakerDescription?: string;
  buyingEntryProbableMindset?: string;
  buyingEntryWhatToSayFirst?: string;
  buyingEntryProofToInclude?: string;
  proposalOfferDirection?: string;
  proposalServicePackage?: string;
  proposalCommercialFraming?: string;
  proposalDeliverables?: string[];
  proposalOutcomePromise?: string;
  proposalWhyNow?: string;
  proposalStructure?: string[];
  executiveSnapshotFitScore?: number;
  executiveSnapshotSalesPotential?: string;
  executiveSnapshotEntryComplexity?: string;
  executiveSnapshotPriority?: string;
  executiveSnapshotBestAngle?: string;
  executiveSnapshotDecisionMaker?: string;
}

export const catalogKeys = {
  countries: () => ["catalog", "countries"] as const,
  industries: () => ["catalog", "industries"] as const,
  totalAll: () => ["catalog", "total"] as const,
  search: (
    countries: string[],
    industries: string[],
    employeeRange: EmployeeRangeFilter | null,
    companyName: string,
    page: number,
    limit: number
  ) =>
    [
      "catalog",
      "search",
      { countries, industries, employeeRange, companyName, page, limit },
    ] as const,
  userCompanies: (userId: string, page: number) => ["catalog", "user", userId, page] as const,
  monthlyStats: (userId: string) => ["catalog", "monthly-stats", userId] as const,
  company: (id: string) => ["catalog", "company", id] as const,
  accessRecord: (userId: string, companyKey: string) =>
    ["catalog", "access-record", userId, companyKey] as const,
};

export function useCatalogCountries() {
  return useQuery({
    queryKey: catalogKeys.countries(),
    queryFn: () => api.get<{ total: number; countries: string[] }>("/companies/catalog-countries"),
    staleTime: 10 * 60 * 1000,
    select: (data) => data.countries,
  });
}

export function useCatalogIndustries() {
  return useQuery({
    queryKey: catalogKeys.industries(),
    queryFn: () =>
      api.get<{ total: number; industries: string[] }>("/companies/catalog-industries"),
    staleTime: 10 * 60 * 1000,
    select: (data) => data.industries,
  });
}

export function useCatalogTotalAll() {
  return useQuery({
    queryKey: catalogKeys.totalAll(),
    queryFn: () => api.get<CatalogSearchResponse>("/companies/catalog-search?page=1&limit=1"),
    staleTime: Infinity,
    select: (data) => data.total,
  });
}

export function useCatalogSearch(
  countries: string[],
  industries: string[],
  employeeRange: EmployeeRangeFilter | null,
  companyName: string,
  page: number,
  limit: number
) {
  return useQuery({
    queryKey: catalogKeys.search(countries, industries, employeeRange, companyName, page, limit),
    queryFn: () => {
      const params = new URLSearchParams();
      countries.forEach((c) => params.append("countries[]", c));
      industries.forEach((i) => params.append("industries[]", i));
      if (employeeRange) {
        params.set("employeeRangesMin", String(employeeRange.min));
        if (employeeRange.max !== undefined) {
          params.set("employeeRangesMax", String(employeeRange.max));
        }
      }
      if (companyName.trim()) {
        params.set("companyName", companyName.trim());
      }
      params.set("page", String(page));
      params.set("limit", String(limit));
      return api.get<CatalogSearchResponse>(`/companies/catalog-search?${params.toString()}`);
    },
    placeholderData: keepPreviousData,
  });
}

export function useUserCatalogCompanies(
  userId: string | undefined,
  accessToken: string | undefined,
  page = 1,
  limit = 10
) {
  return useQuery({
    queryKey: catalogKeys.userCompanies(userId ?? "", page),
    queryFn: () =>
      api.get<UserCompaniesResponse>(
        `/companies/catalog/user/${userId}?page=${page}&limit=${limit}`,
        accessToken
      ),
    enabled: !!userId,
    placeholderData: keepPreviousData,
  });
}

interface MonthlyCreditsStats {
  monthlyUsed: number;
  monthlyLimit: number;
}

export function useMonthlyCreditsStats(
  userId: string | undefined,
  accessToken: string | undefined
) {
  return useQuery({
    queryKey: catalogKeys.monthlyStats(userId ?? ""),
    queryFn: () =>
      api.get<MonthlyCreditsStats>(`/companies/catalog/user/${userId}/monthly-stats`, accessToken),
    enabled: !!userId,
  });
}

export function useCatalogCompany<T>(id: string | undefined) {
  return useQuery({
    queryKey: catalogKeys.company(id ?? ""),
    queryFn: () => api.get<T>(`/companies/catalog/${id}`),
    enabled: !!id,
  });
}

interface UpdateAccessStatusPayload {
  cognitoUserId: string;
  companyKey: string;
  analysisStatus?: AnalysisStatus;
  pdfStatus?: PdfStatus;
  salesOutreachId?: string;
  comparison?: string;
  challenges?: string;
  value?: string;
  contactName?: string;
  contactLastName?: string;
  contactTitle?: string;
  contactCity?: string;
  contactCountry?: string;
  contactLink?: string;
  outreachMessage?: string;
  strategicFitLevel?: string;
  strategicFitSummary?: string;
  strategicFitServices?: Record<string, unknown>[];
  strategicFitObjections?: string[];
  buyingEntryDecisionMaker?: string;
  buyingEntryDecisionMakerDescription?: string;
  buyingEntryProbableMindset?: string;
  buyingEntryWhatToSayFirst?: string;
  buyingEntryProofToInclude?: string;
  proposalOfferDirection?: string;
  proposalServicePackage?: string;
  proposalCommercialFraming?: string;
  proposalDeliverables?: string[];
  proposalOutcomePromise?: string;
  proposalWhyNow?: string;
  proposalStructure?: string[];
  executiveSnapshotFitScore?: number;
  executiveSnapshotSalesPotential?: string;
  executiveSnapshotEntryComplexity?: string;
  executiveSnapshotPriority?: string;
  executiveSnapshotBestAngle?: string;
  executiveSnapshotDecisionMaker?: string;
}

export function useUpdateAccessStatus(userId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateAccessStatusPayload) =>
      api.patch("/companies/catalog/access-status", payload),
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries({ queryKey: ["catalog", "user", userId] });
      }
    },
  });
}

export function useCompanyAccessRecord(
  cognitoUserId: string | undefined,
  companyKey: string | undefined,
  accessToken: string | undefined
) {
  return useQuery({
    queryKey: catalogKeys.accessRecord(cognitoUserId ?? "", companyKey ?? ""),
    queryFn: () =>
      api.get<CompanyAccessRecord>(
        `/companies/catalog/user-access?cognitoUserId=${cognitoUserId}&companyKey=${companyKey}`,
        accessToken
      ),
    enabled: !!cognitoUserId && !!companyKey && !!accessToken,
    retry: false,
  });
}
